import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  GRADE_META,
  AFFINITY_GRADES,
  AffinityBands,
  DEFAULT_AFFINITY_BANDS,
  boundariesFromBands,
} from './bands';
import { TIER_COLORS } from './AffinityNetwork/affinity.constants';

export interface ScoresDistribution {
  total: number;
  byBand: { band: number; count: number }[];
  histogram: { bucket: number; from: number; to: number; count: number }[];
}

interface Props {
  data: ScoresDistribution;
  /** Live affinity bands, so bar colors / filtering track the admin's boundaries. */
  bands?: AffinityBands;
}

// Maps the affinity grade value (0 / 0.4 / 0.7 / 1) to its band metadata key.
const gradeKey = (band: number): string => {
  if (band >= 1) return '1';
  if (band >= 0.7) return '0.7';
  if (band >= 0.4) return '0.4';
  return '0';
};

// Scores-chart emphasis: the dominant "no match" band recedes into a muted
// light slate so the matching bands (L1/L2/L3) carry the color and draw the
// eye. The matching bands keep their vibrant Affinity-Net hues.
const SCORE_BAND_COLOR: Record<string, string> = {
  none: '#CBD5E1',
  L1: TIER_COLORS.L1,
  L2: TIER_COLORS.L2,
  L3: TIER_COLORS.L3,
};
const bandColor = (tier: string): string =>
  SCORE_BAND_COLOR[tier] ?? TIER_COLORS[tier] ?? TIER_COLORS.none;

type Selection = string; // a grade key ('0' | '0.4' | '0.7' | '1') or 'all'

export const ScoresChart: React.FC<Props> = ({ data, bands }) => {
  // Bands tile [0,1] contiguously (SCORE = floor + width·A·Q), so a bucket's
  // score maps to exactly one band. Use the live boundaries when provided.
  const [b1, b2, b3] = boundariesFromBands(bands ?? DEFAULT_AFFINITY_BANDS);
  const scoreGradeKey = (score: number): string => {
    if (score >= b3) return '1';
    if (score >= b2) return '0.7';
    if (score >= b1) return '0.4';
    return '0';
  };

  // Per-band totals (low→high), only bands that actually have scores.
  const visibleBands = useMemo(
    () => [...data.byBand].filter((b) => b.count > 0).sort((a, b) => a.band - b.band),
    [data.byBand],
  );

  // The "best" band = highest affinity with scores; the chart defaults to it so
  // it opens on the matches that matter, not the dominant "no match" bucket.
  const bestGrade = visibleBands.length
    ? gradeKey(visibleBands[visibleBands.length - 1].band)
    : '1';

  const [selected, setSelected] = useState<Selection>(bestGrade);

  const visibleHistogram = useMemo(
    () =>
      selected === 'all'
        ? data.histogram
        : data.histogram.filter((h) => scoreGradeKey((h.from + h.to) / 2) === selected),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.histogram, selected, b1, b2, b3],
  );

  const labels = visibleHistogram.map((h) => h.from.toFixed(2));

  // One stacked series per band so each bar is colored by its band. When a
  // single band is selected only that series has values; the rest are null.
  const series = AFFINITY_GRADES.map((gk) => {
    const meta = GRADE_META[gk];
    return {
      label: meta.label,
      color: bandColor(meta.tier),
      stack: 'scores',
      data: visibleHistogram.map((h) =>
        scoreGradeKey((h.from + h.to) / 2) === gk ? h.count : null,
      ),
    };
  });

  const selectedBand =
    selected === 'all' ? null : visibleBands.find((b) => gradeKey(b.band) === selected);
  const selectedMeta = selected === 'all' ? null : GRADE_META[selected];
  const selectedPct =
    selectedBand && data.total > 0 ? (selectedBand.count / data.total) * 100 : 0;

  return (
    <Box className="flex flex-col gap-4">
      <Box>
        <Typography fontWeight={600} sx={{ fontSize: 14, pb: 0.5 }}>
          Score distribution
        </Typography>
        <Typography color="var(--text-2)" sx={{ fontSize: 13 }}>
          {selected === 'all'
            ? `How the ${data.total.toLocaleString()} expert↔product scores spread across the [0,1] range, colored by affinity band.`
            : `Showing the ${selectedMeta?.label ?? ''} band — ${(selectedBand?.count ?? 0).toLocaleString()} scores (${selectedPct.toFixed(1)}% of all). Bars span this band's slice of the [0,1] score axis.`}
        </Typography>
      </Box>

      <BarChart
        height={260}
        xAxis={[{ data: labels, scaleType: 'band', label: 'Score' }]}
        yAxis={[{ label: 'Scores' }]}
        series={series}
        margin={{ left: 70, right: 20, top: 20, bottom: 50 }}
        slotProps={{ legend: { hidden: true } }}
      />

      {/* Affinity-band filter (also the legend). Click a band to focus the chart
          on it; "All" shows the full distribution. Defaults to the best band. */}
      <Box className="flex flex-col gap-2">
        <Typography fontWeight={600} sx={{ fontSize: 13, color: 'var(--text-2)' }}>
          Filter by affinity band
        </Typography>
        <Box className="flex flex-wrap gap-3">
          <Box
            onClick={() => setSelected('all')}
            className="flex items-center gap-2 cursor-pointer"
            sx={{
              border: '1px solid',
              borderColor: selected === 'all' ? 'var(--text)' : 'var(--border)',
              bgcolor: selected === 'all' ? 'var(--surface-2)' : 'transparent',
              borderRadius: 1.5,
              px: 1.5,
              py: 1,
              transition: 'all 0.12s',
              '&:hover': {
                borderColor: selected === 'all' ? 'var(--text)' : 'var(--muted)',
              },
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>All bands</Typography>
              <Typography color="var(--text-2)" sx={{ fontSize: 12 }}>
                {data.total.toLocaleString()} · 100%
              </Typography>
            </Box>
          </Box>

          {visibleBands.map(({ band, count }) => {
            const gk = gradeKey(band);
            const meta = GRADE_META[gk];
            const color = bandColor(meta?.tier ?? 'none');
            const pct = data.total > 0 ? (count / data.total) * 100 : 0;
            const active = selected === gk;
            return (
              <Box
                key={band}
                onClick={() => setSelected(gk)}
                className="flex items-center gap-2 cursor-pointer"
                sx={{
                  border: '1px solid',
                  borderColor: active ? color : 'var(--border)',
                  bgcolor: active ? `${color}14` : 'transparent',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                  transition: 'all 0.12s',
                  '&:hover': { borderColor: active ? color : 'var(--muted)' },
                }}
              >
                <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: color }} />
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {meta?.label ?? `α=${band}`}
                  </Typography>
                  <Typography color="var(--text-2)" sx={{ fontSize: 12 }}>
                    {count.toLocaleString()} · {pct.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
